import clsx from "clsx";

/**
 * Props interface for the RadioButtonField component.
 */
interface RadioButtonFieldProps {
  /** The value of the radio button */
  value: string;
  /** The label text to display */
  label: string;
  /** Error message to display */
  error?: string;
  /** Additional CSS classes */
  className?: string;
  /** React Hook Form register props */
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  ref?: React.Ref<HTMLInputElement>;
  /** Current form value to determine checked state */
  formValue?: string;
  /** Override default `radio-field-option` test id */
  testId?: string;
}

/**
 * Radio button field component for form inputs.
 *
 * Displays a radio button with label text, styled to match the container
 * design with the radio button positioned on the right side. Designed to
 * work seamlessly with React Hook Form's register method.
 *
 * @example
 * ```tsx
 * <RadioButtonField
 *   {...register("gender")}
 *   value="female"
 *   label="Female"
 *   error={errors.gender?.message}
 * />
 * ```
 */
export default function RadioButtonField({
  value,
  label,
  error,
  className,
  name,
  onChange,
  onBlur,
  ref,
  formValue,
  testId = "radio-field-option",
}: RadioButtonFieldProps) {
  const isChecked = formValue === value;
  return (
    <div className={clsx("relative", className)}>
      {/* Error Message */}
      {error && (
        <div className="absolute -top-6 left-0 text-red-600 text-sm font-medium" data-testid="field-error">
          {error}
        </div>
      )}

      {/* Radio Button Container — test id on label so clicks hit the visible control (input is sr-only) */}
      {/* -m-4 p-4 extends label into Container's padding, making entire visible box clickable */}
      <label
        className="-m-4 p-4 flex items-center justify-between w-full cursor-pointer"
        data-testid={testId}
        data-option-value={value}
      >
        {/* Label Text */}
        <span className="min-w-0 flex-1 break-words pr-2 text-left text-lg font-medium text-black">{label}</span>

        {/* Radio Button */}
        <div className="relative">
          <input
            type="radio"
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            ref={ref}
            checked={isChecked}
            className="sr-only" // Hide the default radio button
          />

          {/* Custom Radio Button Circle */}
          <div
            className={clsx(
              "w-6 h-6 border-2 bg-white rounded-full flex items-center justify-center transition-colors duration-200",
              {
                "border-pink-500": isChecked,
                "border-black": !isChecked,
              }
            )}
          >
            <div
              className={clsx("w-3 h-3 bg-pink-500 rounded-full transition-opacity duration-200", {
                "opacity-100": isChecked,
                "opacity-0": !isChecked,
              })}
            />
          </div>
        </div>
      </label>
    </div>
  );
}
